/**
 * marketHoursPolling — shared gate for community auto-refresh intervals.
 *
 * Two rules from the operator (Feb 15, 2026):
 *   1. Auto-refresh ONLY in production (twylightcrow.com) — never the
 *      preview environment, because preview is for development and
 *      shouldn't hammer the data pipelines.
 *   2. Auto-refresh ONLY during the market window. Yahoo quotes are
 *      15-min delayed, so the live window is 9:30 AM ET → 4:15 PM ET
 *      on weekdays. When a WebSocket layer is added later this can
 *      widen to 7 AM → 8 PM ET.
 *
 * Used by: LeaderboardTab, Community HubTab, TournamentTab.
 */

const PRODUCTION_HOSTS = new Set(['twylightcrow.com', 'www.twylightcrow.com']);

/** True only when the page is being served from production. */
export function isProductionHost() {
  if (typeof window === 'undefined') return false;
  return PRODUCTION_HOSTS.has(window.location.hostname);
}

/**
 * Returns {hour, minute, weekday} for the current moment expressed in
 * America/New_York time. Intl.DateTimeFormat handles DST automatically.
 */
function nowInNewYork() {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return {
      weekday: map.weekday,
      hour: Number(map.hour),
      minute: Number(map.minute),
    };
  } catch {
    // Fallback to local time if the browser doesn't support tz formatting.
    const d = new Date();
    return { weekday: d.toDateString().slice(0, 3), hour: d.getHours(), minute: d.getMinutes() };
  }
}

/** True when current ET time is inside 9:30 → 16:15 on a weekday. */
export function isMarketOpenWindow() {
  const { weekday, hour, minute } = nowInNewYork();
  // Intl 'short' weekday returns 'Mon', 'Tue', ..., 'Sun'.
  if (['Sat', 'Sun'].includes(weekday)) return false;
  const mins = hour * 60 + minute;
  return mins >= (9 * 60 + 30) && mins <= (16 * 60 + 15);
}

/** Single gate the polling hooks consult before firing a setInterval. */
export function shouldAutoRefresh() {
  return isProductionHost() && isMarketOpenWindow();
}
